'use client'

import { useUser } from '@auth0/nextjs-auth0/client'
import { useEffect, useRef } from 'react'

const DEBUG_LOGGING = false

async function isSessionValid(): Promise<boolean> {
    try {
        const response = await fetch('/auth/me', { credentials: 'include' })
        return response.ok
    } catch {
        return false
    }
}

/**
 * useSSEV2 hook for subscribing to multiple SSE event types.
 * Connects to the /api/sse-v2 endpoint for real-time event streaming.
 * @param handlers - An object mapping event types to handler functions. Optionally, provide onMessage for default events.
 * @example
 *   useSSEV2({
 *     [InternalEventType.CANDIDATE_PROCESSED]: (event) => { ... },
 *     [InternalEventType.JOB_APPLICATION_PROCESSED]: (event) => { ... },
 *     [InternalEventType.JOB_APPLICATION_DELETED]: (event) => { ... },
 *     [InternalEventType.CANDIDATE_DELETED]: (event) => { ... },
 *     onMessage: (event) => { ... }
 *   })
 */
export function useSSEV2(handlers: { [eventType: string]: (event: MessageEvent) => void }) {
    const { user, isLoading } = useUser()
    const userRef = useRef(user)
    userRef.current = user

    const handlersRef = useRef(handlers)
    handlersRef.current = handlers
    const retryDelayRef = useRef(1000) // Start with 1s
    const maxReconnectAttempts = 5 // Max retry attempts after failure
    const maxTotalConnectionAttempts = 10 // Hard cap on total connection attempts
    const retryCountRef = useRef(0)
    const totalConnectionAttemptsRef = useRef(0) // Track all connection attempts
    const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const eventSourceRef = useRef<EventSource | null>(null)
    const isConnectingRef = useRef(false)
    const lastErrorTimeRef = useRef<number>(0)
    const duplicateConnectionDetectedRef = useRef(false)

    useEffect(() => {
        let isUnmounted = false
        let mountedAt = Date.now()

        if (DEBUG_LOGGING) console.log('[useSSEv2] useEffect running - Component mounted/updated')

        function cleanup() {
            if (eventSourceRef.current) {
                const eventSource = eventSourceRef.current
                const readyState = eventSource.readyState
                if (DEBUG_LOGGING) console.log(`[useSSEv2] Cleaning up EventSource (readyState: ${readyState})`)

                // Remove all event listeners
                Object.entries(handlersRef.current).forEach(([eventType, handler]) => {
                    if (eventType === 'onMessage') {
                        eventSource.onmessage = null
                    } else {
                        const wrappedHandler = (eventSource as any)._wrappedHandlers?.[eventType] || handler
                        eventSource.removeEventListener(eventType, wrappedHandler)
                    }
                })

                // Always attempt to close - close() is safe to call even if already closed
                // Check readyState again right before closing to ensure we don't close an already-closed connection unnecessarily
                if (eventSource.readyState !== EventSource.CLOSED) {
                    try {
                        eventSource.close()
                        if (DEBUG_LOGGING) console.log('[useSSEv2] EventSource.close() called')
                    } catch (error) {
                        console.error('[useSSEv2] Error closing EventSource:', error)
                    }
                }
                eventSourceRef.current = null
                if (DEBUG_LOGGING) console.log('[useSSEv2] EventSource closed and cleaned up')
            }
            isConnectingRef.current = false
        }

        function scheduleReconnect(delay: number) {
            if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current)

            if (DEBUG_LOGGING)
                console.log(
                    `[useSSEv2] Reconnecting in ${delay}ms (attempt ${retryCountRef.current}/${maxReconnectAttempts}, total attempts: ${totalConnectionAttemptsRef.current}/${maxTotalConnectionAttempts})`
                )

            if (totalConnectionAttemptsRef.current < maxTotalConnectionAttempts) {
                reconnectTimeoutRef.current = setTimeout(() => {
                    if (!isUnmounted) connect()
                }, delay)
            } else {
                console.error(`[useSSEv2] Hard limit of ${maxTotalConnectionAttempts} total connection attempts reached. Stopping reconnection.`)
            }
        }

        async function handleConnectionError(eventSource: EventSource, err: Event) {
            if (isUnmounted) return

            const readyState = eventSource.readyState
            isConnectingRef.current = false

            if (duplicateConnectionDetectedRef.current) {
                if (DEBUG_LOGGING) console.log('[useSSEv2] Not reconnecting due to duplicate connection.')
                return
            }

            if (!userRef.current) {
                cleanup()
                return
            }

            if (readyState === EventSource.CLOSED) {
                const sessionValid = await isSessionValid()
                if (isUnmounted || !userRef.current || !sessionValid) {
                    cleanup()
                    return
                }
            }

            console.error(`[useSSEv2] SSE connection error (readyState: ${readyState})`, err)

            if (readyState === EventSource.CLOSED && !isUnmounted) {
                if (DEBUG_LOGGING) console.log('[useSSEv2] Connection closed by server or network issue')
            }

            if (!isUnmounted) {
                const now = Date.now()
                const timeSinceLastError = now - lastErrorTimeRef.current
                lastErrorTimeRef.current = now

                const baseDelay = timeSinceLastError < 5000 ? retryDelayRef.current * 2 : retryDelayRef.current
                const delay = Math.min(baseDelay, 16000)

                retryCountRef.current += 1
                retryDelayRef.current = delay

                scheduleReconnect(delay)
            } else if (DEBUG_LOGGING) {
                console.log('[useSSEv2] Not reconnecting - component unmounted')
            }
        }

        function connect() {
            if (isUnmounted) {
                if (DEBUG_LOGGING) console.log('[useSSEv2] Skipping connect - component unmounted')
                return
            }

            if (!userRef.current) {
                if (DEBUG_LOGGING) console.log('[useSSEv2] Skipping connect - user not authenticated')
                return
            }

            if (isConnectingRef.current) {
                if (DEBUG_LOGGING) console.log('[useSSEv2] Skipping connect - already connecting')
                return
            }

            totalConnectionAttemptsRef.current += 1

            if (retryCountRef.current >= maxReconnectAttempts) {
                console.error(`[useSSEv2] Max retry attempts (${maxReconnectAttempts}) reached. Stopping SSE reconnection.`)
                return
            }

            if (totalConnectionAttemptsRef.current >= maxTotalConnectionAttempts) {
                console.error(`[useSSEv2] Hard limit of ${maxTotalConnectionAttempts} total connection attempts reached. Stopping all SSE connections.`)
                return
            }

            if (duplicateConnectionDetectedRef.current) {
                if (DEBUG_LOGGING) console.log('[useSSEv2] Duplicate connection detected. Not reconnecting.')
                return
            }

            isConnectingRef.current = true

            if (DEBUG_LOGGING) console.log('[useSSEv2] Initializing SSE connection - Cleaning up existing connection')
            cleanup()

            const eventSource = new EventSource('/api/sse-v2')
            eventSourceRef.current = eventSource
            if (DEBUG_LOGGING)
                console.log(
                    `[useSSEv2] EventSource created (attempt ${totalConnectionAttemptsRef.current}/${maxTotalConnectionAttempts}, readyState: ${eventSource.readyState})`
                )

            eventSource.addEventListener('duplicate_connection', (event) => {
                if (DEBUG_LOGGING) console.log('[useSSEv2] Duplicate connection detected:', event)
                duplicateConnectionDetectedRef.current = true
                cleanup()
            })

            eventSource.onopen = () => {
                const timeSinceMount = Date.now() - mountedAt
                if (DEBUG_LOGGING) console.log(`[useSSEv2] SSE connection opened (${timeSinceMount}ms after mount, readyState: ${eventSource.readyState})`)
                retryDelayRef.current = 1000
                retryCountRef.current = 0
                isConnectingRef.current = false
                lastErrorTimeRef.current = 0
            }

            eventSource.onerror = (err) => {
                void handleConnectionError(eventSource, err)
            }

            Object.entries(handlersRef.current).forEach(([eventType, handler]) => {
                if (eventType === 'onMessage') {
                    eventSource.onmessage = (event) => {
                        if (DEBUG_LOGGING) console.log(`[useSSEv2] onMessage event received:`, event)
                        handler(event)
                    }
                } else {
                    const wrappedHandler = (event: MessageEvent) => {
                        if (DEBUG_LOGGING) console.log(`[useSSEv2] Event received for type: ${eventType}`, event)
                        handler(event)
                    }
                    eventSource.addEventListener(eventType, wrappedHandler)
                    ;(eventSource as any)._wrappedHandlers = (eventSource as any)._wrappedHandlers || {}
                    ;(eventSource as any)._wrappedHandlers[eventType] = wrappedHandler
                    if (DEBUG_LOGGING) console.log(`[useSSEv2] Subscribed to event: ${eventType}`)
                }
            })
        }

        const effectCleanup = () => {
            const timeSinceMount = Date.now() - mountedAt
            if (DEBUG_LOGGING) console.log(`[useSSEv2] Unmounting SSE connection (component was mounted for ${timeSinceMount}ms) - Cleaning up SSE connection`)
            isUnmounted = true
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current)
                reconnectTimeoutRef.current = null
            }
            cleanup()
        }

        if (isLoading) {
            return effectCleanup
        }

        if (!user) {
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current)
                reconnectTimeoutRef.current = null
            }
            cleanup()
            return effectCleanup
        }

        retryDelayRef.current = 1000
        retryCountRef.current = 0
        totalConnectionAttemptsRef.current = 0
        duplicateConnectionDetectedRef.current = false
        lastErrorTimeRef.current = 0

        const connectTimeout = setTimeout(() => {
            if (!isUnmounted) {
                connect()
            }
        }, 100)

        return () => {
            clearTimeout(connectTimeout)
            effectCleanup()
        }
    }, [isLoading, user?.sub])
}
